import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
    Button,
    TextInput,
    Textarea,
    NumberInput,
    ActionIcon,
    Checkbox,
    Badge,
    Tooltip,
} from '@mantine/core';
import {
    IconUsers,
    IconTrash,
    IconExternalLink,
    IconPlus,
    IconMinus,
    IconBabyCarriage,
    IconClipboardList,
    IconSearch,
    IconArrowsExchange,
    IconCheck,
    IconX,
    IconAlertCircle,
    IconInfoCircle,
} from '@tabler/icons-react';
import { ReservationPaxGroup, Person, PAX_CATEGORY_COLORS, Reservation } from '@turizi/shared';
import CardModal from '../card-modal';
import { PERSONS_PATH } from '@/lib/navigation';
import { useBulkCreatePersons, useReservation } from '@/hooks/api-hooks';
import CreateOrSelectPerson from '../create-or-select-person-component';
import { ShowPerson } from '../snippets';
import { notify } from '@/lib/notify';

interface PaxGroupModalProps {
    opened: boolean;
    onClose: () => void;
    reservation: Reservation;
    group?: ReservationPaxGroup | null;
    onSave: (data: Partial<ReservationPaxGroup>) => Promise<void>;
    onDelete?: (groupId: number) => Promise<void>;
    isLoading?: boolean;
}

export type HeadcountCategory = 'adults' | 'children' | 'seniors' | 'guests' | 'infants';

interface HeadcountCategoryConfig {
    key: HeadcountCategory;
    labelKey: string;
}

const HEADCOUNT_CATEGORIES: HeadcountCategoryConfig[] = [
    { key: 'adults', labelKey: 'adults' },
    { key: 'children', labelKey: 'children' },
    { key: 'seniors', labelKey: 'seniors' },
    { key: 'infants', labelKey: 'infants' },
    { key: 'guests', labelKey: 'guests' },
];

export default function PaxGroupModal({
    opened,
    onClose,
    reservation,
    group,
    onSave,
    onDelete,
    isLoading = false,
}: PaxGroupModalProps) {
    const { t } = useTranslation();
    const {
        data: resData
    } = useReservation(reservation.id);
    // const {
    //     data: allPaxGroups = []
    // } = useReservationPaxGroups(reservationId);

    const [paxsList, setPaxsList] = useState<Person[]>([]);
    const [name, setName] = useState('');

    const [adults, setAdults] = useState<number | string>('');
    const [children, setChildren] = useState<number | string>('');
    const [infants, setInfants] = useState<number | string>('');
    const [seniors, setSeniors] = useState<number | string>('');
    const [guests, setGuests] = useState<number | string>('');
    const [notes, setNotes] = useState('');
    // const [addToAllBookings, setAddToAllBookings] = useState(false);

    // Bulk paste state
    const [showBulkPaste, setShowBulkPaste] = useState(false);
    const [bulkRawText, setBulkRawText] = useState('');
    const [autoSyncHeadcount, setAutoSyncHeadcount] = useState(true);
    const [bulkMessages, setBulkMessages] = useState<Array<{
        row?: number;
        type: 'error' | 'warning' | 'info';
        code: string;
        params?: Record<string, any>;
        line?: string;
    }>>([]);
    const [paxListSearch, setPaxListSearch] = useState('');

    const bulkCreatePersonsMutation = useBulkCreatePersons();

    const isEdit = Boolean(group?.id);

    const defaultGroupName = '';

    // Populate when opening
    useEffect(() => {
        if (opened) {
            // setAddToAllBookings(false);
            setShowBulkPaste(false);
            setBulkRawText('');
            setBulkMessages([]);
            setPaxListSearch('');
            if (group) {
                let initialItems: Person[] = [];
                initialItems = group.persons?.map(p => p) || [];
                setPaxsList(initialItems);
                setName(group.name || '');

                setAdults(group.adults || '');
                setChildren(group.children || '');
                setInfants(group.infants || '');
                setSeniors(group.seniors || '');
                setGuests(group.guests || '');
                setNotes(group.observations || '');
            } else {
                setPaxsList([]);
                setName('');
                setAdults('');
                setChildren('');
                setInfants('');
                setSeniors('');
                setGuests('');
                setNotes('');
            }
        }
    }, [opened, group, resData?.data?.persons]);

    const seatPaxs = (Number(adults) || 0) + (Number(children) || 0) + (Number(seniors) || 0) + (Number(guests) || 0);
    // const hasBookings = (resData?.data?.service_bookings?.length || 0) > 0;

    // Add person to items and sync state
    const addPersonItem = (pax: Person) => {
        if (pax.id && paxsList.some((p) => p.id === pax.id)) return;
        const nextList = [...paxsList, pax];
        setPaxsList(nextList);
    };

    const syncHeadcountFromList = (list: Person[]) => {
        let sumAdults = 0;
        let sumChildren = 0;
        let sumInfants = 0;
        let sumSeniors = 0;
        let sumGuests = 0;

        for (const p of list) {
            const pAdults = Number(p.adults) || 0;
            const pChildren = Number(p.children) || 0;
            const pInfants = Number(p.infants) || 0;
            const pSeniors = Number(p.seniors) || 0;
            const pGuests = Number(p.guests) || 0;
            const total = pAdults + pChildren + pInfants + pSeniors + pGuests;

            if (total === 0) {
                sumAdults += 1;
            } else {
                sumAdults += pAdults;
                sumChildren += pChildren;
                sumInfants += pInfants;
                sumSeniors += pSeniors;
                sumGuests += pGuests;
            }
        }

        setAdults(sumAdults || '');
        setChildren(sumChildren || '');
        setInfants(sumInfants || '');
        setSeniors(sumSeniors || '');
        setGuests(sumGuests || '');
    };

    const handleConfirmBulkImport = async () => {
        if (!bulkRawText.trim()) return;
        setBulkMessages([]);

        try {
            const res = await bulkCreatePersonsMutation.mutateAsync(bulkRawText);
            const createdPersons = res?.data || [];
            const existingIds = new Set(paxsList.map((p) => p.id).filter(Boolean));
            const merged = [...paxsList];
            for (const p of createdPersons) {
                if (p && p.id && !existingIds.has(p.id)) {
                    existingIds.add(p.id);
                    merged.push(p);
                }
            }
            setPaxsList(merged);

            if (autoSyncHeadcount) {
                syncHeadcountFromList(merged);
            }

            if (Array.isArray(res?.messages) && res.messages.length > 0) {
                setBulkMessages(res.messages);
            }

            setBulkRawText('');
            // setShowBulkPaste(false);
            // notify.success(t('success'), t('bulk_passengers_added_count', { count: createdPersons.length }));
        } catch (err: any) {
            console.error('Error bulk creating passengers:', err);
            const backendMessages = err?.response?.data?.messages;
            const backendErrorCode = err?.response?.data?.error;
            if (Array.isArray(backendMessages) && backendMessages.length > 0) {
                setBulkMessages(backendMessages);
            } else if (typeof backendErrorCode === 'string') {
                setBulkMessages([{ type: 'error', code: backendErrorCode }]);
            } else {
                notify.error(t('error'), t('error'));
            }
        }
    };

    const handleSyncHeadcountToPaxList = () => {
        if (paxsList.length === 0) return;
        syncHeadcountFromList(paxsList);
    };

    const filteredPaxsList = useMemo(() => {
        const q = paxListSearch.trim().toLowerCase();
        if (!q) return paxsList.map((pax, idx) => ({ pax, idx }));
        return paxsList
            .map((pax, idx) => ({ pax, idx }))
            .filter(({ pax }) =>
                (pax.name || '').toLowerCase().includes(q) ||
                (pax.document_number || '').toLowerCase().includes(q) ||
                (pax.phone || '').toLowerCase().includes(q) ||
                (pax.email || '').toLowerCase().includes(q)
            );
    }, [paxsList, paxListSearch]);

    // Remove passenger from group
    const handleRemovePax = (indexToRemove: number) => {
        const itemToRemove = paxsList[indexToRemove];
        if (!itemToRemove) return;

        const newItems = paxsList.filter((_, i) => i !== indexToRemove);
        setPaxsList(newItems);
    };

    // Submit / Save
    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();

        const finalAdults = Number(adults) || 0;
        const finalChildren = Number(children) || 0;
        const finalInfants = Number(infants) || 0;
        const finalSeniors = Number(seniors) || 0;
        const finalGuests = Number(guests) || 0;
        const resolvedName = (name || defaultGroupName).trim();

        const uniquePersonIds = Array.from(
            new Set((paxsList || []).map((p) => Number(p.id)).filter(Boolean))
        );

        const personIds = uniquePersonIds;

        const members = uniquePersonIds
            .map(pid => ({
                pax_group_id: group?.id,
                person_id: pid,
            }));

        const payload: Partial<ReservationPaxGroup> = {
            name: resolvedName,
            adults: finalAdults,
            children: finalChildren,
            infants: finalInfants,
            seniors: finalSeniors,
            guests: finalGuests,
            person_ids: personIds,
            members,
        };

        await onSave(payload);
    };

    const headcountValues: Record<HeadcountCategory, number | string> = {
        adults,
        children,
        seniors,
        guests,
        infants,
    };

    const setHeadcountValue = (category: HeadcountCategory, val: number | string) => {
        const num = Number(val);
        const nextVal: number | string = val === '' || isNaN(num) || num <= 0 ? '' : num;
        if (category === 'adults') setAdults(nextVal);
        else if (category === 'children') setChildren(nextVal);
        else if (category === 'seniors') setSeniors(nextVal);
        else if (category === 'guests') setGuests(nextVal);
        else if (category === 'infants') setInfants(nextVal);
    };

    const renderHeadcountRow = (key: HeadcountCategory, labelKey: string) => {
        const value = headcountValues[key];
        const numericValue = Number(value) || 0;
        const colors = PAX_CATEGORY_COLORS[key];
        return (
            <div
                key={key}
                className="flex items-center justify-between px-3 py-1 bg-gray-50 rounded-md"
            >
                <span className={`text-xs font-medium ${colors?.text || 'text-slate-700'}`}>{t(labelKey)}</span>
                <div className="flex items-center gap-1">
                    <ActionIcon
                        size="md"
                        radius="xl"
                        variant="light"
                        color="black"
                        onClick={() => setHeadcountValue(key, numericValue - 1)}
                        disabled={numericValue <= 0}
                    >
                        <IconMinus size={11} stroke={3} />
                    </ActionIcon>
                    <NumberInput
                        value={value}
                        onChange={(val) => setHeadcountValue(key, val)}
                        min={0}
                        hideControls
                        size="sm"
                        className="w-15 font-bold"
                        styles={{ input: { textAlign: 'center' } }}
                    />
                    <ActionIcon
                        size="md"
                        radius="xl"
                        variant="light"
                        color="black"
                        onClick={() => setHeadcountValue(key, numericValue + 1)}
                    >
                        <IconPlus size={11} stroke={3} />
                    </ActionIcon>
                </div>
            </div>
        );
    };

    return (
        <CardModal
            opened={opened}
            onClose={onClose}
            size="90%"
            header={
                <div className="flex items-center justify-between w-full pr-4">
                    <div className="flex items-center gap-2">
                        <IconUsers size={20} className="text-slate-700" />
                        <span className="font-bold text-base text-slate-800">
                            {isEdit ? t('edit_pax_group') : t('add_pax_group')}
                        </span>
                    </div>
                </div>
            }
            footer={
                <div className="w-full flex items-center justify-between">
                    <div>
                        {isEdit && onDelete && group?.id && (
                            <Button
                                color="red"
                                variant="subtle"
                                size="xs"
                                leftSection={<IconTrash size={14} />}
                                onClick={() => group.id && onDelete(group.id)}
                                disabled={isLoading}
                            >
                                {t('delete')}
                            </Button>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="subtle"
                            color="black"
                            size="xs"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            color="black"
                            size="xs"
                            onClick={() => handleSubmit()}
                            loading={isLoading}
                        >
                            {t('save_group')}
                        </Button>
                    </div>
                </div>
            }
        >
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">

                {/*  Column: Group Name, Numbers Breakdown & Notes */}
                <div className="md:col-span-3 space-y-4">
                    {/* 1. Group Name */}
                    <div className="space-y-1">
                        <TextInput
                            label={t('pax_group_name')}
                            placeholder={defaultGroupName}
                            value={name}
                            onChange={(e) => setName(e.currentTarget.value)}
                            size="sm"
                            className='font-bold'
                        />
                    </div>

                    {/* 2. Headcount Numbers Breakdown - Single column: Diff Paxs vs Infants */}
                    <div className="space-y-3 border-t border-slate-200 pt-3">
                        {/* Paxs / Seats */}
                        <div className="space-y-1.5">
                            {HEADCOUNT_CATEGORIES.map(({ key, labelKey }) => renderHeadcountRow(key, labelKey))}
                        </div>

                        {/* Total Summary Row */}
                        <div className='flex gap-2 justify-center items-center flex-wrap'>
                            <div className='flex items-center gap-1 solid-box-border-orange'>
                                <IconUsers size={14} className="text-slate-400" />
                                <div className='text-lg font-bold'>
                                    {seatPaxs}
                                </div>
                            </div>
                            <div className='flex items-center gap-1 solid-box-border-orange'>
                                <IconBabyCarriage size={14} className="text-slate-400" />
                                <div className='text-lg font-bold'>{Number(infants) || 0}</div>
                            </div>
                            {paxsList.length > 0 && seatPaxs !== paxsList.length && (
                                <Tooltip label={t('sync_headcount_to_pax_list')} withArrow>
                                    <Button
                                        size="compact-xs"
                                        variant="light"
                                        color="indigo"
                                        leftSection={<IconArrowsExchange size={12} />}
                                        onClick={handleSyncHeadcountToPaxList}
                                    >
                                        {t('match_count', { count: paxsList.length })}
                                    </Button>
                                </Tooltip>
                            )}
                        </div>
                    </div>

                    {/* 3. Assign to all bookings option when creating */}
                    {/* {!isEdit && hasBookings && (
                        <div className="pt-2 border-t border-slate-200">
                            <Checkbox
                                label={t('add_to_all_bookings')}
                                checked={addToAllBookings}
                                onChange={(e) => setAddToAllBookings(e.currentTarget.checked)}
                                size="xs"
                                color="dark"
                            />
                        </div>
                    )} */}
                </div>

                {/*  Column: Paxs list & Add passenger */}
                <div className="md:col-span-9 space-y-3">

                    <div className="flex items-center gap-2">
                        <div className="text-xs font-semibold text-slate-700">
                            {showBulkPaste ? t('bulk_paste_passengers') : t('search_or_add_new_passenger')}
                        </div>
                        <Button
                            size="compact-xs"
                            variant={showBulkPaste ? 'filled' : 'light'}
                            color={showBulkPaste ? 'dark' : 'indigo'}
                            leftSection={showBulkPaste ? <IconX size={12} /> : <IconClipboardList size={13} />}
                            onClick={() => setShowBulkPaste((prev) => !prev)}
                        >
                            {showBulkPaste ? t('single_passenger_mode') : t('bulk_paste_passengers')}
                        </Button>
                    </div>

                    {showBulkPaste ? (
                        <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                            <div className="text-[11px] text-slate-500">
                                {t('bulk_paste_instructions')}
                            </div>
                            <Textarea
                                value={bulkRawText}
                                onChange={(e) => {
                                    setBulkRawText(e.currentTarget.value);
                                    if (bulkMessages.length > 0) setBulkMessages([]);
                                }}
                                placeholder={t('bulk_paste_placeholder')}
                                minRows={4}
                                maxRows={8}
                                autosize
                                size="xs"
                                styles={{
                                    input: {
                                        fontFamily: 'monospace',
                                        fontSize: 12,
                                    },
                                }}
                            />

                            {bulkMessages.length > 0 && (
                                <div className="space-y-1 max-h-36 overflow-y-auto">
                                    {bulkMessages.map((msg, idx) => {
                                        const isError = msg.type === 'error';
                                        const isWarning = msg.type === 'warning';
                                        return (
                                            <div
                                                key={`${msg.code}-${msg.row ?? idx}-${idx}`}
                                                className={`flex items-start gap-2 px-2.5 py-1.5 rounded-lg border text-xs ${isError
                                                    ? 'bg-red-50 border-red-200 text-red-700'
                                                    : isWarning
                                                        ? 'bg-amber-50 border-amber-200 text-amber-800'
                                                        : 'bg-blue-50 border-blue-200 text-blue-700'
                                                    }`}
                                            >
                                                {isError || isWarning ? (
                                                    <IconAlertCircle size={14} className="shrink-0 mt-0.5" />
                                                ) : (
                                                    <IconInfoCircle size={14} className="shrink-0 mt-0.5" />
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-medium">
                                                        {String(t(msg.code, msg.params))}
                                                    </div>
                                                    {msg.line && (
                                                        <div className="font-mono text-[11px] opacity-80 truncate">
                                                            {msg.line}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            <div className="flex items-center justify-between gap-2">
                                <Checkbox
                                    size="xs"
                                    color="dark"
                                    checked={autoSyncHeadcount}
                                    onChange={(e) => setAutoSyncHeadcount(e.currentTarget.checked)}
                                    label={t('auto_update_adults_count')}
                                />
                                <div className="flex items-center gap-2">
                                    <Button
                                        size="compact-xs"
                                        color="black"
                                        leftSection={<IconCheck size={14} />}
                                        loading={bulkCreatePersonsMutation.isPending}
                                        disabled={!bulkRawText.trim()}
                                        onClick={handleConfirmBulkImport}
                                    >
                                        {t('add_passengers')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <CreateOrSelectPerson
                            onSelect={addPersonItem}
                        />
                    )}

                    <div className="flex items-center justify-between gap-2 border-t pt-2 border-slate-200">
                        <div className="flex items-center gap-1.5">
                            <span className="text-xs font-semibold text-slate-700">
                                {t('pax_list')}
                            </span>
                            <Badge size="xs" variant="light" color="gray">
                                {paxsList.length}
                            </Badge>
                        </div>

                        {paxsList.length > 6 && (
                            <TextInput
                                size="xs"
                                value={paxListSearch}
                                onChange={(e) => setPaxListSearch(e.currentTarget.value)}
                                placeholder={t('search_passengers')}
                                leftSection={<IconSearch size={12} className="text-slate-400" />}
                                rightSection={
                                    paxListSearch ? (
                                        <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => setPaxListSearch('')}>
                                            <IconX size={10} />
                                        </ActionIcon>
                                    ) : null
                                }
                                className="w-48"
                            />
                        )}
                    </div>

                    {/* List of Passengers in this Group */}
                    <div className="space-y-1.5 max-h-[420px] overflow-y-auto subtle-scrollbar pr-0.5">
                        {filteredPaxsList.map(({ pax, idx }) => {
                            return (
                                <div
                                    key={pax.id ? `pax-${pax.id}-${idx}` : `pax-${idx}`}
                                    className="p-2 bg-slate-100 rounded-lg flex items-center justify-between gap-2.5 shadow-2xs hover:border-slate-300 transition-colors"
                                >
                                    {/* Left details */}
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                        <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-600 font-semibold text-xs flex items-center justify-center shrink-0">
                                            {idx + 1}
                                        </div>

                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-1.5">
                                                {pax.id ? (
                                                    <Link
                                                        to={`${PERSONS_PATH}/${pax.id}`}
                                                        target="_blank"
                                                        className="min-w-0 flex items-center gap-1 group"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <ShowPerson
                                                            person={pax}
                                                            hideIcon
                                                            className='font-bold text-blue-500 text-xs group-hover:underline'
                                                        />
                                                        <IconExternalLink size={11} className="text-slate-400 shrink-0" />
                                                    </Link>
                                                ) : (
                                                    <span className="font-semibold text-xs text-slate-900 truncate">
                                                        {pax.name}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-1.5 shrink-0">
                                        <ActionIcon
                                            size="sm"
                                            variant="subtle"
                                            color="gray"
                                            onClick={() => handleRemovePax(idx)}
                                            title={t('delete')}
                                        >
                                            <IconTrash size={14} />
                                        </ActionIcon>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>


            </div>
        </CardModal>
    );
}
